import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotifications } from 'app/application/application'
import { useConversion, useTreatments } from 'app/audiences/application'
import { Audience, ConversionsPerTreatments, Treatment } from 'app/audiences/types'
import { Connection as ConnectionComponent, PagedTable } from 'app/common/presentation'
import { getFormerDay } from 'app/common/presentation/getFormerDay'

import { PercentageIndicator, Section, Table } from 'components'

import styles from './Treatments.module.scss'

interface TreatmentsProps {
  audience: Audience
  className?: string
}

const pageSize = 5

const now = new Date()
const oneWeekAgo = getFormerDay(7)
const eightDaysAgo = getFormerDay(8)
const twoWeeksAgo = getFormerDay(14)

const last7Days = [oneWeekAgo, now]
const previousSevenDays = [twoWeeksAgo, eightDaysAgo]

const determineConversionChange = (
  treatmentId: string,
  firstPeriod: ConversionsPerTreatments | undefined,
  secondPeriod: ConversionsPerTreatments | undefined,
) => {
  if (firstPeriod && secondPeriod) {
    const firstPeriodConversion = firstPeriod[treatmentId]
    const secondPeriodConversion = secondPeriod[treatmentId]

    if (firstPeriodConversion && secondPeriodConversion) {
      return firstPeriodConversion / secondPeriodConversion
    }
  }

  return undefined
}

const Treatments = ({ audience, className }: TreatmentsProps) => {
  const { t } = useTranslation('audiences')

  const { createNotification, te } = useNotifications()
  const { getConversions } = useConversion(audience.id)
  const { getTreatments } = useTreatments()

  const [treatments, setTreatments] = useState<Treatment[]>([])
  const [lastSevenDaysConversion, setLastSevenDaysConversion] = useState<ConversionsPerTreatments>()
  const [previousSevenDaysConversion, setPreviousSevenDaysConversion] =
    useState<ConversionsPerTreatments>()

  useEffect(() => {
    void getTreatments(
      { id: audience.config.treatments },
      1,
      audience.config.treatments.length,
    ).then((result) =>
      result.ok
        ? setTreatments(result.val)
        : createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          }),
    )
  }, [audience.config.treatments, createNotification, getTreatments, te])

  useEffect(() => {
    if (treatments.length) {
      void getConversions(last7Days[0], last7Days[1]).then(setLastSevenDaysConversion)
      void getConversions(previousSevenDays[0], previousSevenDays[1]).then(
        setPreviousSevenDaysConversion,
      )
    }
  }, [getConversions, treatments])

  return (
    <div className={className}>
      <Section name={t('details.treatments.title')} />
      <PagedTable
        className={styles.treatments}
        columns={[
          t('details.treatments.columns.name'),
          t('details.treatments.columns.campaign'),
          t('details.treatments.columns.connection'),
          t('details.treatments.columns.conversion'),
        ]}
        data={treatments}
        pageSize={pageSize}
        renderRow={(treatment, index) => (
          <Table.Row key={index}>
            <Table.Cell>{treatment.name}</Table.Cell>
            <Table.Cell>{treatment.campaign.name}</Table.Cell>
            <Table.Cell>
              <ConnectionComponent connection={treatment.connection} />
            </Table.Cell>
            <Table.Cell>
              <PercentageIndicator
                percentage={
                  determineConversionChange(
                    treatment.id,
                    previousSevenDaysConversion,
                    lastSevenDaysConversion,
                  ) ?? 0
                }
              />
            </Table.Cell>
          </Table.Row>
        )}
      />
    </div>
  )
}

export default Treatments
